/**
 * Family co-owners. Members listed here are users in this household (tenant),
 * so each one sees ALL the household's cars — current and future — with full
 * access. Billing/transfer stays with the primary owner. The owner can invite
 * (create), rename (update), and remove (delete) members.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Layout } from '@/components/Layout';
import { Icon } from '@/components/kol/Icon';
import { useAuthStore } from '@/stores/auth.store';
import {
  listFamilyMembers,
  inviteFamilyMember,
  updateFamilyMember,
  removeFamilyMember,
  ApiError,
  type FamilyMember,
} from '@/lib/api';

export function FamilyPage() {
  const { t, i18n } = useTranslation();
  const [members, setMembers] = useState<FamilyMember[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await listFamilyMembers();
        if (!cancelled) setMembers(data.members);
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : t('common.error'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh, t]);

  const canManage = members?.some((m) => m.isSelf && m.isPrimaryOwner) ?? false;
  const reload = () => setRefresh((n) => n + 1);

  return (
    <Layout
      title={t('family.title', { defaultValue: 'המשפחה' })}
      trailing={
        <Link
          to="/kol"
          aria-label={t('common.back', { defaultValue: 'חזרה' })}
          className="w-10 h-10 rounded-full border border-carnaf-line bg-carnaf-paper-elevated grid place-items-center text-carnaf-ink-muted shadow-kol-1"
        >
          <Icon name="arrow" size={19} />
        </Link>
      }
    >
      <p className="text-body-sm text-carnaf-ink-muted mb-4 leading-relaxed">
        {t('family.intro', {
          defaultValue: 'כל מי שכאן רואה את כל הרכבים שלך — עכשיו ובעתיד — עם גישה מלאה. רק את/ה מנהל/ת חיוב.',
        })}
      </p>

      {error && (
        <p role="alert" className="text-body-sm text-carnaf-red mb-4">
          {error}
        </p>
      )}
      {members === null && !error && (
        <p className="text-carnaf-ink-muted">{t('common.loading', { defaultValue: 'טוען…' })}</p>
      )}

      {members && (
        <ul className="space-y-2.5 mb-6">
          {members.map((m) => (
            <MemberRow key={m.id} member={m} canManage={canManage} onChanged={reload} />
          ))}
        </ul>
      )}

      {canManage && <InviteForm lang={i18n.language} onInvited={reload} />}
    </Layout>
  );
}

function MemberRow({
  member,
  canManage,
  onChanged,
}: {
  member: FamilyMember;
  canManage: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const updateSessionName = useAuthStore((s) => s.updateName);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(member.name ?? '');
  const [busy, setBusy] = useState(false);

  const display = member.name?.trim() || member.email;
  // Identifiers must render cleanly (Principle 11): one phone OR one email,
  // LTR-embedded, never a doubled/concatenated string. Drop a phone value that
  // is actually an email-shaped duplicate of the address, and dedupe equal values.
  const email = member.email?.trim() || '';
  const rawPhone = member.phone?.trim() || '';
  const phone = rawPhone && !rawPhone.includes('@') && rawPhone !== email ? rawPhone : '';
  const removable = canManage && !member.isPrimaryOwner && !member.isSelf;
  // The owner can rename ANY member, including their own (primary-owner) row.
  const renamable = canManage;

  async function saveName() {
    setBusy(true);
    try {
      const next = name.trim() || undefined;
      await updateFamilyMember(member.id, { name: next });
      // Renaming yourself must refresh the home greeting immediately.
      if (member.isSelf) updateSessionName(next ?? null);
      setEditing(false);
      onChanged();
    } finally {
      setBusy(false);
    }
  }
  async function remove() {
    setBusy(true);
    try {
      await removeFamilyMember(member.id);
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="rounded-2xl bg-white border border-carnaf-line p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {editing ? (
            <div className="flex items-center gap-2">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={80}
                className="flex-1 rounded-lg border border-carnaf-line px-3 py-1.5 text-body-sm focus:outline-none focus:ring-2 focus:ring-carnaf-teal"
              />
              <button
                onClick={saveName}
                disabled={busy}
                className="text-body-sm font-semibold text-carnaf-teal disabled:opacity-50"
              >
                {t('common.save', { defaultValue: 'שמירה' })}
              </button>
            </div>
          ) : (
            <p className="font-semibold text-carnaf-ink truncate">{display}</p>
          )}
          {/* line-clamp (not truncate): an LTR identifier inside this RTL
              container has its START eaten by single-line truncate
              ("…il.com" instead of "name@mail.com"). Clamp to two lines and
              keep the value bidi-isolated. */}
          <p className="text-[15px] text-carnaf-ink-muted mt-0.5 line-clamp-2">
            <bdi dir="ltr" className="align-bottom">
              {email}
            </bdi>
            {phone && (
              <bdi dir="ltr" className="align-bottom ms-1.5">
                · {phone}
              </bdi>
            )}
          </p>
          <div className="flex items-center gap-1.5 mt-1.5">
            {member.isPrimaryOwner && <Badge tone="teal">{t('family.owner_badge', { defaultValue: 'בעלים' })}</Badge>}
            {member.isSelf && <Badge tone="muted">{t('family.you', { defaultValue: 'את/ה' })}</Badge>}
            {!member.hasSignedIn && (
              <Badge tone="amber">{t('family.pending', { defaultValue: 'ממתין/ה לכניסה' })}</Badge>
            )}
          </div>
        </div>
        {(renamable || removable) && !editing && (
          <div className="flex items-center gap-1.5 flex-none">
            {renamable && (
              <button
                type="button"
                aria-label={t('family.rename', { defaultValue: 'שינוי שם' })}
                onClick={() => setEditing(true)}
                className="w-9 h-9 rounded-full bg-carnaf-paper-elevated text-carnaf-ink-muted grid place-items-center"
              >
                <Icon name="edit" size={16} />
              </button>
            )}
            {removable && (
              <button
                type="button"
                aria-label={t('family.remove', { defaultValue: 'הסרה' })}
                onClick={remove}
                disabled={busy}
                className="w-9 h-9 rounded-full bg-carnaf-paper-elevated text-carnaf-red grid place-items-center disabled:opacity-50"
              >
                <Icon name="trash" size={16} />
              </button>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

function Badge({ tone, children }: { tone: 'teal' | 'muted' | 'amber'; children: React.ReactNode }) {
  const cls =
    tone === 'teal'
      ? 'bg-carnaf-teal-soft text-carnaf-teal'
      : tone === 'amber'
        ? 'bg-carnaf-amber-soft text-carnaf-amber-ink'
        : 'bg-carnaf-paper-sunk text-carnaf-ink-muted';
  return <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${cls}`}>{children}</span>;
}

function InviteForm({ lang, onInvited }: { lang: string; onInvited: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setOk(false);
    setBusy(true);
    try {
      await inviteFamilyMember({
        name: name.trim() || undefined,
        email: email.trim(),
        phone: phone.trim(),
        preferredLanguage: lang.split('-')[0],
      });
      setName('');
      setEmail('');
      setPhone('');
      setOk(true);
      onInvited();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  const input =
    'w-full rounded-xl border border-carnaf-line bg-white px-4 py-3 text-body focus:outline-none focus:ring-2 focus:ring-carnaf-teal';

  return (
    <section className="rounded-2xl bg-white border border-carnaf-line p-5 space-y-3">
      <h2 className="font-kol-display text-[18px] font-medium text-carnaf-ink">
        {t('family.invite_title', { defaultValue: 'הוספת בן/בת משפחה' })}
      </h2>
      <p className="text-[15px] text-carnaf-ink-muted leading-relaxed">
        {t('family.invite_hint', {
          defaultValue: 'נשלח להם קישור כניסה במייל. מרגע שייכנסו, הם רואים את כל הרכבים שלך.',
        })}
      </p>
      <form onSubmit={submit} className="space-y-3">
        <input
          className={input}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('family.invite_name', { defaultValue: 'שם (לא חובה)' })}
          maxLength={80}
        />
        <input
          className={input}
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('family.invite_email', { defaultValue: 'אימייל' })}
          dir="ltr"
        />
        <input
          className={input}
          required
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder={t('family.invite_phone', { defaultValue: 'טלפון' })}
          dir="ltr"
        />
        {error && (
          <p role="alert" className="text-body-sm text-carnaf-red">
            {error}
          </p>
        )}
        {ok && (
          <p className="text-body-sm text-carnaf-teal">{t('family.invited_ok', { defaultValue: 'ההזמנה נשלחה' })}</p>
        )}
        <button
          type="submit"
          disabled={busy || !email.trim() || !phone.trim()}
          className="w-full rounded-2xl bg-carnaf-teal text-white font-semibold py-3 disabled:opacity-50"
        >
          {busy
            ? t('common.loading', { defaultValue: 'שולח…' })
            : t('family.invite_submit', { defaultValue: 'שליחת הזמנה' })}
        </button>
      </form>
    </section>
  );
}
